import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/components/auth/AuthContext'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import type { LiveCall } from '@/pages/authenticated/home/LiveCallRow'
import { useTutorialProgress } from '@/components/tutorials/useTutorialProgress'
import { CoachMarks, isOnScreen, type CoachStep } from './CoachMarks'
import {
  FIRST_RUN_CHANGED,
  readFirstRun,
  readHiddenForSession,
  trackFirstRun,
  writeFirstRun,
  writeHiddenForSession,
  type FirstRunState,
} from './firstRunStore'
import { SAMPLE_CALLER, SAMPLE_CALL_ID, SAMPLE_END_AT, SAMPLE_SUMMARY, sampleStatus, sampleTurns } from './sampleCall'

/** The four steps, all on Home. A step whose target is not on the page is skipped. */
export const HOME_TOUR: CoachStep[] = [
  {
    target: 'section[aria-label="For you"] > header',
    side: 'bottom',
    title: 'Everything waiting on you lands here.',
    body: 'Each row is a doorway: it opens the decision in Needs your attention with the evidence beside it.',
  },
  {
    target: 'a[href="/aria"]',
    side: 'right',
    title: 'Ask Brain about your business.',
    body: 'What needs attention today, who still owes you, a draft invoice for a finished job.',
  },
  {
    target: '[data-search-anchor]',
    side: 'bottom',
    title: 'Press / to find anything.',
    body: 'A customer, a job, an invoice number — results update as you type.',
  },
  {
    target: 'button[aria-label="Help"]',
    side: 'bottom',
    title: 'Help is one press away.',
    body: 'Replay this tour or message us from here, any time.',
  },
]

interface FirstRunContextValue {
  state: FirstRunState
  /** Home's setup banner is hidden for this session ("Hide for now"). */
  hidden: boolean
  tourActive: boolean
  startTour: () => void
  /** Help → Replay the tour: goes Home first if needed, then starts. */
  replayTour: () => void
  /** Help → Reset tutorial progress: clear the current user's tour, sample and
   *  watched explanation videos, then reopen Help at its index (Get set up). */
  resetTutorial: () => void
  /** "See how Aria answers": goes to the Calls page and plays the sample there. */
  playSample: () => void
  /** The sample call while it is on the Calls page, in the host live-call shape. */
  sampleCall: LiveCall | null
  /** "Dismiss sample" in the detail's footer: the sample leaves the timeline. */
  dismissSample: () => void
  hideForSession: (hidden: boolean) => void
}

const FirstRunContext = createContext<FirstRunContextValue | null>(null)

const HOME = '/home'
const CALLS = '/calls'

function visibleSteps(): CoachStep[] {
  return HOME_TOUR.filter((step) => Array.from(document.querySelectorAll<HTMLElement>(step.target)).some(isOnScreen))
}

interface SampleRun {
  startedAt: number
  shown: number
  ended: boolean
}

/**
 * Owns the first-run state for the signed-in user (design system Components
 * · 09): what has been seen, the tour, and the sample call that plays out on
 * the Calls page. Nothing here starts on its own — a checklist item or the
 * Help drawer does — and a tour that was skipped or completed is never shown
 * again for this tour version.
 */
export function FirstRunProvider({
  children,
  onReset,
}: {
  children: ReactNode
  /** Called after Reset tutorial progress — the layout opens Help's index. */
  onReset?: () => void
}) {
  const { user } = useAuth()
  const userId = user?.userId ?? user?.id ?? ''
  const owner = user?.name?.trim().split(/\s+/)[0] || 'you'
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const reduced = useMediaQuery('(prefers-reduced-motion: reduce)')
  const [state, setState] = useState<FirstRunState>(() => readFirstRun(userId))
  const [hidden, setHidden] = useState(() => readHiddenForSession(userId))
  const [steps, setSteps] = useState<CoachStep[] | null>(null)
  const [sample, setSample] = useState<SampleRun | null>(null)
  const [pending, setPending] = useState(false)
  const { reset: resetTutorials } = useTutorialProgress()
  const onResetRef = useRef(onReset)
  onResetRef.current = onReset

  // Stay in step with the store (another tab, or a write elsewhere).
  useEffect(() => {
    const sync = () => {
      setState(readFirstRun(userId))
      setHidden(readHiddenForSession(userId))
    }
    sync()
    window.addEventListener(FIRST_RUN_CHANGED, sync)
    window.addEventListener('storage', sync)
    return () => {
      window.removeEventListener(FIRST_RUN_CHANGED, sync)
      window.removeEventListener('storage', sync)
    }
  }, [userId])

  const startTour = useCallback(() => {
    // Home's lazy chunk and its first paint may still be arriving: try for a
    // moment before giving up quietly.
    let tries = 0
    const attempt = () => {
      const found = visibleSteps()
      if (found.length > 0) {
        setSteps(found)
        trackFirstRun('tour_started', { userId, steps: found.length })
        return
      }
      if (tries++ < 12) window.setTimeout(attempt, 250)
    }
    attempt()
  }, [userId])

  const replayTour = useCallback(() => {
    trackFirstRun('tour_replay', { userId, from: pathname })
    if (pathname === HOME) {
      startTour()
    } else {
      setPending(true)
      navigate(HOME)
    }
  }, [navigate, pathname, startTour, userId])

  // Stays where the person is: Help reopens at its index with Get set up on
  // top (it used to return Home and unfold the checklist there), and every
  // page's explanation video counts as unwatched again.
  const resetTutorial = useCallback(() => {
    setSteps(null)
    setSample(null)
    setPending(false)
    setState(writeFirstRun(userId, { tour: 'unseen', sample: false }))
    writeHiddenForSession(userId, false)
    setHidden(false)
    resetTutorials()
    trackFirstRun('tutorial_reset', { userId })
    onResetRef.current?.()
  }, [resetTutorials, userId])

  useEffect(() => {
    if (!pending || pathname !== HOME) return
    setPending(false)
    startTour()
  }, [pathname, pending, startTour])

  const endTour = useCallback(
    (completed: boolean) => {
      setSteps(null)
      setState(writeFirstRun(userId, { tour: completed ? 'completed' : 'skipped' }))
      trackFirstRun(completed ? 'tour_completed' : 'tour_skipped', { userId })
    },
    [userId],
  )

  const onStep = useCallback((index: number) => trackFirstRun('tour_step', { userId, index }), [userId])

  /* ── The sample call plays on the Calls page: it arrives as a live call on
     the timeline, its detail opens, the turns land at a reading pace, and it
     ends handled. Leaving the Calls page ends the sample. ── */
  const playSample = useCallback(() => {
    setSample({ startedAt: Date.now(), shown: 0, ended: false })
    trackFirstRun('sample_started', { userId })
    navigate(`${CALLS}/${SAMPLE_CALL_ID}`)
  }, [navigate, userId])

  const startedAt = sample?.startedAt
  useEffect(() => {
    if (startedAt == null) return undefined
    const turns = sampleTurns(owner)
    // Under reduced motion the whole transcript is there at once; pacing is
    // motion here, not information.
    const at = (seconds: number) => (reduced ? 0 : seconds * 1000)
    const timers = turns.map((turn, i) =>
      window.setTimeout(() => setSample((run) => (run && !run.ended ? { ...run, shown: i + 1 } : run)), at(turn.at)),
    )
    timers.push(
      window.setTimeout(
        () => {
          setSample((run) => (run ? { ...run, shown: turns.length, ended: true } : run))
          setState(writeFirstRun(userId, { sample: true }))
          trackFirstRun('sample_played', { userId })
        },
        reduced ? 1200 : at(SAMPLE_END_AT),
      ),
    )
    return () => timers.forEach((t) => window.clearTimeout(t))
  }, [owner, reduced, startedAt, userId])

  useEffect(() => {
    if (sample && !pathname.startsWith(CALLS)) setSample(null)
  }, [pathname, sample])

  const dismissSample = useCallback(() => {
    setSample((run) => {
      if (run) trackFirstRun('sample_dismissed', { userId })
      return null
    })
  }, [userId])

  const sampleCall = useMemo<LiveCall | null>(
    () =>
      sample
        ? {
            id: SAMPLE_CALL_ID,
            customerName: SAMPLE_CALLER,
            customerId: null,
            phone: '',
            status: sampleStatus(sample.shown),
            startedAt: new Date(sample.startedAt).toISOString(),
            href: `${CALLS}/${SAMPLE_CALL_ID}`,
            sample: { turns: sampleTurns(owner), shown: sample.shown, ended: sample.ended, summary: SAMPLE_SUMMARY },
          }
        : null,
    [owner, sample],
  )

  const hideForSession = useCallback(
    (next: boolean) => {
      writeHiddenForSession(userId, next)
      setHidden(next)
    },
    [userId],
  )

  const value = useMemo<FirstRunContextValue>(
    () => ({ state, hidden, tourActive: steps !== null, startTour, replayTour, resetTutorial, playSample, sampleCall, dismissSample, hideForSession }),
    [dismissSample, hidden, hideForSession, playSample, replayTour, resetTutorial, sampleCall, startTour, state, steps],
  )

  return (
    <FirstRunContext.Provider value={value}>
      {children}
      {steps && <CoachMarks steps={steps} onEnd={endTour} onStep={onStep} />}
    </FirstRunContext.Provider>
  )
}

export function useFirstRun(): FirstRunContextValue {
  const ctx = useContext(FirstRunContext)
  if (!ctx) throw new Error('useFirstRun must be used inside FirstRunProvider')
  return ctx
}

/** For surfaces that may render outside the provider (tests, previews). */
export function useOptionalFirstRun(): FirstRunContextValue | null {
  return useContext(FirstRunContext)
}
